/**
 * Settings configuration types for the v3 dashboard.
 * Used by both the leads worker (backend) and dashboard (frontend).
 */

import type { Plan } from './plans'

// ============================================================
// Setting Keys
// ============================================================

export const SETTING_KEYS = {
  offer: 'offer_config',
  messages: 'message_config',
  cta: 'cta_config',
  content: 'content_config',
  senderVoice: 'sender_voice_config',
  workflowSafety: 'workflow_safety_config',
  faq: 'faq_entries',
  objections: 'objection_entries',
} as const

/** Minimum plan required to access each setting group */
export const SETTINGS_PLAN_REQUIRED: Record<string, Plan> = {
  [SETTING_KEYS.offer]: 'starter',
  [SETTING_KEYS.messages]: 'starter',
  [SETTING_KEYS.cta]: 'starter',
  // 2026-07-27 owner spec: content posting opens to every plan. Starter gets
  // 5 posts/week with 1 automated; Pro gets 10/week with 1 automated per day.
  [SETTING_KEYS.content]: 'starter',
  [SETTING_KEYS.senderVoice]: 'pro',
  [SETTING_KEYS.workflowSafety]: 'starter',
  [SETTING_KEYS.faq]: 'starter',
  [SETTING_KEYS.objections]: 'starter',
}

// ============================================================
// FAQ Config — questions a lead might ask + how the AI should answer
// ============================================================

/**
 * One FAQ entry. The AI consults these on inbound replies (task='reply')
 * and uses a matching answer as the source of truth when the lead asks
 * something similar.
 */
export type FaqEntry = {
  readonly id: string
  readonly question: string
  readonly answer: string
}

export type FaqConfig = {
  readonly entries: readonly FaqEntry[]
}

export const DEFAULT_FAQ: FaqConfig = {
  entries: [],
}

/**
 * Runtime type guard — used by PUT /settings/faq to validate body shape.
 * Trims strings and enforces non-empty question + answer for each entry,
 * caps total entries at 100 (sanity bound on prompt-injected context),
 * and per-field length limits matched to what realistically fits in the
 * AI worker's system prompt without blowing the context budget.
 */
export function isFaqConfig(value: unknown): value is FaqConfig {
  if (typeof value !== 'object' || value === null) return false
  const v = value as { entries?: unknown }
  if (!Array.isArray(v.entries)) return false
  if (v.entries.length > 100) return false
  for (const entry of v.entries) {
    if (typeof entry !== 'object' || entry === null) return false
    const e = entry as { id?: unknown; question?: unknown; answer?: unknown }
    if (typeof e.id !== 'string' || e.id.length === 0 || e.id.length > 64) return false
    if (typeof e.question !== 'string' || e.question.trim().length === 0 || e.question.length > 500) return false
    if (typeof e.answer !== 'string' || e.answer.trim().length === 0 || e.answer.length > 4000) return false
  }
  return true
}

// ============================================================
// Objection Config — objections the lead might raise + how the AI counters
// ============================================================

/**
 * One Objection entry — a likely-to-be-raised concern with the response
 * the AI should mirror when it comes up. Distinct from FAQ in two ways:
 *   1. FAQ is for direct questions ("How much does it cost?"). Objections
 *      are for stated concerns / pushback ("This feels like an AI message",
 *      "We've tried this and it didn't work").
 *   2. Objections are matched at REPLY time even when the lead's message
 *      doesn't look like a question, because objections are often
 *      statements, not questions.
 */
export type ObjectionEntry = {
  readonly id: string
  readonly objection: string
  readonly response: string
}

export type ObjectionConfig = {
  readonly entries: readonly ObjectionEntry[]
}

/**
 * Default seed. We always ship the "this feels like AI" objection because
 * it's the single most common pushback we see across F&F. Users can edit
 * or remove it; the response below is the wording Eli + TJ approved
 * 2026-06-03 — direct, confident, doesn't apologize for being automated.
 */
export const DEFAULT_OBJECTIONS: ObjectionConfig = {
  entries: [
    {
      id: 'obj_default_ai_detection',
      objection: 'This feels like an AI automated message to me.',
      response:
        'I promise you the amount of sales closed and time saved far outweigh the amount of people who can detect this is AI.',
    },
  ],
}

export function isObjectionConfig(value: unknown): value is ObjectionConfig {
  if (typeof value !== 'object' || value === null) return false
  const v = value as { entries?: unknown }
  if (!Array.isArray(v.entries)) return false
  if (v.entries.length > 100) return false
  for (const entry of v.entries) {
    if (typeof entry !== 'object' || entry === null) return false
    const e = entry as { id?: unknown; objection?: unknown; response?: unknown }
    if (typeof e.id !== 'string' || e.id.length === 0 || e.id.length > 64) return false
    if (typeof e.objection !== 'string' || e.objection.trim().length === 0 || e.objection.length > 500) return false
    if (typeof e.response !== 'string' || e.response.trim().length === 0 || e.response.length > 4000) return false
  }
  return true
}

// ============================================================
// Offer & Pitch Config
// ============================================================

export type OfferConfig = {
  readonly offerDescription: string
  readonly primaryPainPoint: string
  readonly painPoints: readonly string[]
  readonly socialProof: string
  readonly differentiator: string
  readonly coreOfferOneLiner: string
  readonly hasCaseStudy: boolean
  readonly caseStudyOneLiner: string
  readonly hasShareableResource: boolean
  readonly resourceDescription: string
  readonly resourceLink: string
  readonly authorityProof: string
  readonly targetAudience?: string
}

export const DEFAULT_OFFER: OfferConfig = {
  offerDescription: '',
  primaryPainPoint: '',
  painPoints: [],
  socialProof: '',
  differentiator: '',
  coreOfferOneLiner: '',
  hasCaseStudy: false,
  caseStudyOneLiner: '',
  hasShareableResource: false,
  resourceDescription: '',
  resourceLink: '',
  authorityProof: '',
  targetAudience: '',
}

// ============================================================
// Message Settings
// ============================================================

export type MessageTone = 'professional_casual' | 'casual' | 'professional' | 'formal'

export type MessageConfig = {
  readonly messageTone: MessageTone
  readonly exampleMessage: string
  readonly customInstructions: string
  readonly messagesPerHour: number
  readonly maxDailyMessages: number
  readonly followUp1Days: number
  readonly followUp2Days: number
  readonly followUp3Days: number
}

export const DEFAULT_MESSAGES: MessageConfig = {
  messageTone: 'professional_casual',
  exampleMessage: '',
  customInstructions: '',
  messagesPerHour: 5,
  maxDailyMessages: 30,
  followUp1Days: 3,
  followUp2Days: 7,
  followUp3Days: 14,
}

// ============================================================
// CTA Config
// ============================================================

export type CtaType =
  | 'book_demo'
  | 'book_discovery_call'
  | 'book_strategy_call'
  | 'free_audit'
  | 'send_to_page'
  | 'send_to_trial'
  | 'send_to_event'
  | 'direct_conversation'
  | 'calendar_link'

export type CtaConfig = {
  readonly ctaType: CtaType
  readonly ctaLink: string
  readonly desiredAction: string
  readonly ctaPrimaryLabel: string
  readonly ctaPrimaryUrl: string
  readonly ctaSecondaryLabel: string
  readonly ctaSecondaryUrl: string
}

export const DEFAULT_CTA: CtaConfig = {
  ctaType: 'book_demo',
  ctaLink: '',
  desiredAction: '',
  ctaPrimaryLabel: '',
  ctaPrimaryUrl: '',
  ctaSecondaryLabel: '',
  ctaSecondaryUrl: '',
}

// ============================================================
// Content Config (all plans since 2026-07-27; auto-poster cadence is
// clamped per plan by autoPostsPerWeek)
// ============================================================

export type ContentConfig = {
  readonly contentTopics: readonly string[]
  readonly contentTone: string
  readonly postingFrequency: string
  readonly contentPillars: readonly string[]
  readonly contentCtaMode: string
  readonly posterEnabled: boolean
  readonly posterFrequency: string
  readonly posterAutoApprove: boolean
  /** Standing style guidance for generated posts, saved from the auto-poster
   *  preview/feedback flow. Applied to every generation for this user. */
  readonly posterStyleNotes?: string
}

export const DEFAULT_CONTENT: ContentConfig = {
  contentTopics: [],
  contentTone: 'professional',
  postingFrequency: 'daily',
  contentPillars: [],
  contentCtaMode: 'subtle',
  posterEnabled: false,
  posterFrequency: 'daily',
  posterAutoApprove: false,
  posterStyleNotes: '',
}

// ============================================================
// Sender Voice Config (Pro+)
// ============================================================

export type SenderVoiceConfig = {
  readonly profileSummary: string
  readonly writingSample1: string
  readonly writingSample2: string
  readonly povAreas: readonly string[]
  readonly credibilityBullets: readonly string[]
  readonly doNotSay: readonly string[]
}

export const DEFAULT_SENDER_VOICE: SenderVoiceConfig = {
  profileSummary: '',
  writingSample1: '',
  writingSample2: '',
  povAreas: [],
  credibilityBullets: [],
  doNotSay: [],
}

// ============================================================
// Workflow & Safety Config
// ============================================================

/**
 * Manual message review mode (owner request 2026-07-22):
 *   'all'     — every AI-written message (outreach, follow-ups, replies)
 *               waits in the inbox review queue until approved.
 *   'flagged' — only outreach drafts the quality gate scores in the
 *               send_with_review band wait for approval; high-scoring
 *               drafts and AI replies send automatically.
 *   'none'    — fully automatic (default, matches historical behavior).
 * Connection invites carry no text (deliberate) and are never held.
 */
export type ReviewMode = 'all' | 'flagged' | 'none'

export const REVIEW_MODES: readonly ReviewMode[] = ['all', 'flagged', 'none']

export type WorkflowSafetyConfig = {
  readonly warmupEnabled: boolean
  readonly aiReplyEnabled: boolean
  readonly posterEnabled: boolean
  readonly commentDmEnabled: boolean
  readonly maxDailyConnections: number
  readonly maxDailyProfileViews: number
  readonly weekendEnabled: boolean
  readonly rampupEnabled: boolean
  readonly rampupDays: number
  readonly offHoursStart: number
  readonly offHoursEnd: number
  readonly reviewMode: ReviewMode
}

export const DEFAULT_WORKFLOW_SAFETY: WorkflowSafetyConfig = {
  warmupEnabled: true,
  aiReplyEnabled: false,
  posterEnabled: false,
  commentDmEnabled: false,
  maxDailyConnections: 20,
  maxDailyProfileViews: 50,
  weekendEnabled: false,
  rampupEnabled: true,
  rampupDays: 14,
  offHoursStart: 22,
  offHoursEnd: 7,
  reviewMode: 'none',
}

// ============================================================
// Validators
// ============================================================

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((item) => typeof item === 'string')
}

export function isOfferConfig(v: unknown): v is OfferConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return (
    typeof o.offerDescription === 'string' &&
    typeof o.primaryPainPoint === 'string' &&
    isStringArray(o.painPoints) &&
    typeof o.socialProof === 'string' &&
    typeof o.differentiator === 'string' &&
    typeof o.coreOfferOneLiner === 'string' &&
    typeof o.hasCaseStudy === 'boolean' &&
    typeof o.hasShareableResource === 'boolean'
  )
}

export function isMessageConfig(v: unknown): v is MessageConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return (
    typeof o.messageTone === 'string' &&
    typeof o.messagesPerHour === 'number' &&
    typeof o.maxDailyMessages === 'number' &&
    typeof o.followUp1Days === 'number' &&
    typeof o.followUp2Days === 'number' &&
    typeof o.followUp3Days === 'number'
  )
}

export function isCtaConfig(v: unknown): v is CtaConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return typeof o.ctaType === 'string' && typeof o.ctaLink === 'string'
}

export function isContentConfig(v: unknown): v is ContentConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return (
    isStringArray(o.contentTopics) &&
    typeof o.contentTone === 'string' &&
    typeof o.posterEnabled === 'boolean' &&
    (o.posterStyleNotes === undefined || typeof o.posterStyleNotes === 'string')
  )
}

export function isSenderVoiceConfig(v: unknown): v is SenderVoiceConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return (
    typeof o.profileSummary === 'string' &&
    isStringArray(o.povAreas) &&
    isStringArray(o.credibilityBullets) &&
    isStringArray(o.doNotSay)
  )
}

export function isWorkflowSafetyConfig(v: unknown): v is WorkflowSafetyConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return (
    typeof o.warmupEnabled === 'boolean' &&
    typeof o.aiReplyEnabled === 'boolean' &&
    typeof o.maxDailyConnections === 'number' &&
    typeof o.maxDailyProfileViews === 'number' &&
    // Optional for backward compatibility: blobs saved before 0031 (and
    // stale dashboard tabs) omit it; the PUT route backfills from the
    // stored blob so an old client can't silently reset the mode.
    (o.reviewMode === undefined || REVIEW_MODES.includes(o.reviewMode as ReviewMode))
  )
}
